import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactHowler from "react-howler";
import notificationSound from "../assets/sounds/notification.mp3";

export interface NotificationData {
  chatId: string | number;
  chatName: string;
  message?: string;
}

interface GlobalNotificationContextType {
  showNotification: (notification: NotificationData) => void;
}

const GlobalNotificationContext = createContext<
  GlobalNotificationContextType | undefined
>(undefined);

export const useGlobalNotification = () => {
  const context = useContext(GlobalNotificationContext);
  if (!context) {
    throw new Error(
      "useGlobalNotification must be used within a GlobalNotificationProvider",
    );
  }
  return context;
};

interface GlobalNotificationProviderProps {
  children: ReactNode;
}

export const GlobalNotificationProvider: React.FC<
  GlobalNotificationProviderProps
> = ({ children }) => {
  const { t } = useTranslation();
  const [notification, setNotification] = useState<NotificationData | null>(
    null,
  );
  const [playSound, setPlaySound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Pede permissão para notificações nativas
    if ("Notification" in window && Notification.permission !== "granted") {
      Notification.requestPermission();
    }
  }, []);

  const showNotification = (notif: NotificationData) => {
    setNotification(notif);
    setPlaySound(true);

    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(
        t("notification.new_message_title", { chatName: notif.chatName }),
        { body: notif.message || t("notification.default_message_body") },
      );
    }
  };

  const handleSoundEnd = () => {
    setPlaySound(false);
  };

  return (
    <GlobalNotificationContext.Provider value={{ showNotification }}>
      {children}
      {playSound && (
        <ReactHowler
          src={notificationSound}
          playing={true}
          onEnd={handleSoundEnd}
        />
      )}
    </GlobalNotificationContext.Provider>
  );
};
